import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, ArrowDown, PlaneTakeoff } from "lucide-react";
import { AIRPORTS } from "@/data";
import { AIRPORT_OPTIONS, useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

export function AirportsMap() {
  const { t, lang, isAr } = useLang();
  const [selected, setSelected] = useState(AIRPORTS[0]);

  const book = () => {
    const opt = AIRPORT_OPTIONS.find((o) => o.startsWith(selected.code === "TIP" ? "Mitiga" : selected.name_en.split(" ")[0])) ||
      AIRPORT_OPTIONS.find((o) => o.includes(selected.code)) || AIRPORT_OPTIONS[0];
    window.dispatchEvent(new CustomEvent("was:book-airport", { detail: opt }));
  };

  return (
    <section id="airports" data-testid="airports-section" className="relative py-24 lg:py-32 bg-[#081020] border-y border-blue-500/10 overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 max-w-3xl"
        >
          <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400">{t("airports_eyebrow")}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white" data-testid="airports-title">{t("airports_title")}</h2>
          <p className="mt-4 text-base sm:text-lg text-slate-400">{t("airports_sub")}</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-3"
            data-testid="libya-airports-map-container"
          >
            <div data-always-dark className="rounded-3xl border border-blue-500/15 bg-[#0B1526] p-3 sm:p-6 shadow-[0_20px_60px_rgba(2,6,17,0.4)]">
            <svg viewBox="0 0 100 100" className="w-full h-auto max-h-[520px]" role="img" aria-label="Libya airports coverage map">
              <defs>
                <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                </radialGradient>
              </defs>
              <path
                d="M7 30 L13 21 L21 19 L27 22 L33 21 L38 24 L41 31 L47 33 L52 28 L58 24 L62 20 L67 17 L73 15 L79 17 L85 21 L91 25 L92 34 L93 50 L92 88 L70 91 L40 92 L18 90 L10 78 L7 58 L6 42 Z"
                fill="rgba(30, 64, 175, 0.10)"
                stroke="rgba(59, 130, 246, 0.45)"
                strokeWidth="0.35"
                strokeLinejoin="round"
              />
              <g className="radar-sweep" style={{ transformBox: "view-box", transformOrigin: "32px 26px" }}>
                <path d="M32 26 L32 8 A18 18 0 0 1 41 10 Z" fill="url(#pinGlow)" />
              </g>
              {AIRPORTS.map((a) => {
                const active = selected.code === a.code;
                return (
                  <g
                    key={a.code}
                    data-testid={`airport-pin-${a.code.toLowerCase()}`}
                    onClick={() => setSelected(a)}
                    className="cursor-pointer"
                    role="button"
                    aria-label={a.name_en}
                  >
                    <circle cx={a.x} cy={a.y} r="5" fill="url(#pinGlow)" opacity={active ? 1 : 0.5} />
                    {a.isHq && <circle cx={a.x} cy={a.y} r="2.2" fill="none" stroke="#60A5FA" strokeWidth="0.4" className="radar-ping" />}
                    <circle
                      cx={a.x}
                      cy={a.y}
                      r={active ? 2 : 1.4}
                      fill={active ? "#FFFFFF" : a.isHq ? "#3B82F6" : "#2563EB"}
                      stroke="#0B1526"
                      strokeWidth="0.45"
                      style={{ transition: "all 0.25s" }}
                    />
                    <text
                      x={a.x}
                      y={a.y - 3.2}
                      textAnchor="middle"
                      fontSize="3.1"
                      fill={active ? "#FFFFFF" : "#93C5FD"}
                      fontFamily="JetBrains Mono, monospace"
                      fontWeight="600"
                    >
                      {a.code}
                    </text>
                  </g>
                );
              })}
            </svg>
            </div>
          </motion.div>

          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.code + lang}
                data-testid="airport-detail-card"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="rounded-2xl border border-blue-500/20 bg-[#0B1526] p-7 lg:p-8 shadow-[0_20px_60px_rgba(2,6,17,0.5)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono-tag text-4xl font-bold text-white" dir="ltr">{selected.code}</span>
                  <div className="flex items-center gap-2">
                    {selected.isHq && (
                      <span className="rounded-full bg-blue-600 px-3 py-1 text-[11px] font-bold text-white" data-testid="airport-hq-badge">{t("hq_badge")}</span>
                    )}
                    <span className="font-mono-tag text-xs text-blue-400 border border-blue-500/30 rounded-full px-2.5 py-1" dir="ltr">{selected.icao}</span>
                  </div>
                </div>
                <h3 className="mt-3 text-2xl font-bold text-white">{selected[`name_${lang}`]}</h3>
                <p className="mt-1 text-sm text-slate-400">{selected[`city_${lang}`]} · {selected[`type_${lang}`]}</p>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-blue-950/40 border border-blue-500/15 p-3.5">
                    <div className="font-mono-tag text-[10px] uppercase tracking-widest text-blue-400">{t("airport_runway")}</div>
                    <div className="mt-1 font-bold text-white" dir="ltr">{selected.runway}</div>
                  </div>
                  <div className="rounded-xl bg-blue-950/40 border border-blue-500/15 p-3.5">
                    <div className="font-mono-tag text-[10px] uppercase tracking-widest text-blue-400">{t("airport_status")}</div>
                    <div className="mt-1 flex items-center gap-1.5 font-bold text-emerald-400">
                      <Activity size={14} />
                      {selected[`status_${lang}`]}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="font-mono-tag text-[10px] uppercase tracking-widest text-blue-400 mb-2.5">{t("airport_services")}</div>
                  <div className="flex flex-wrap gap-2">
                    {selected[`services_${lang}`].map((s) => (
                      <span key={s} className="rounded-full bg-blue-600/10 border border-blue-500/20 px-3 py-1 text-xs text-blue-200">{s}</span>
                    ))}
                  </div>
                </div>

                <button
                  data-testid="airport-book-button"
                  onClick={book}
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-bold text-white hover:bg-blue-500 transition-colors duration-200 shadow-[0_0_24px_rgba(37,99,235,0.4)]"
                >
                  <PlaneTakeoff size={16} />
                  {t("airport_book")}
                  <ArrowDown size={15} className="opacity-70" />
                </button>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
